import { ChatBubbleOutlined } from '@mui/icons-material';
import { Paper, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ChatComposer, type ComposerSize } from './chatComposer';
import ChatHeader from './chatHeader';
import ChatMessageList from './chatMessageList';
import DocumentPreview from 'components/v2/common/documentPreview';
import Result from 'components/v2/common/Result';
import ViewTicket from 'views/v2/Dashboards-B2B/components/backOfficeFlow/tickets/components/viewTicket';

import api from 'api';
import { ACTIONS } from 'constant';
import { parseReplySuggestion } from 'helpers/chatReplySuggestion';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import type { ChatMessage, ChatReference, ReplySuggestionResponse, TicketInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface ChatInterfaceProps {
    users: string[];
    messages: ChatMessage[];
    isLoading: boolean;
    isSending: boolean;
    isSuggestingReply: boolean;
    hasAccess: boolean;
    isBackOffice: boolean;
    canAccessBackoffice: boolean;
    canRetryReplySuggestion: boolean;
    canSuggestReply: boolean;
    hasBackofficeAssigned: boolean;
    hasActiveApplication: boolean;
    references?: ChatReference[];
    onToggle: () => void;
    onSend: (text: string, references?: ChatReference[]) => void;
    onSuggestReply: () => Promise<ReplySuggestionResponse | null>;
    onDelete: (message: ChatMessage) => void;
}

const ChatInterface = ({
    users,
    messages,
    isLoading,
    isSending,
    isSuggestingReply,
    hasAccess,
    isBackOffice,
    canAccessBackoffice,
    canRetryReplySuggestion,
    canSuggestReply,
    hasBackofficeAssigned,
    hasActiveApplication,
    references,
    onToggle,
    onSend,
    onSuggestReply,
    onDelete,
}: ChatInterfaceProps) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const [composerSize, setComposerSize] = useState<ComposerSize>('compact');
    const [messageText, setMessageText] = useState('');
    const [suggestionHint, setSuggestionHint] = useState('');
    const [viewTicket, setViewTicket] = useState<TicketInterface | null>(null);
    const [previewFile, setPreviewFile] = useState<{ fileUrl: string; fileName: string } | null>(
        null
    );
    const suggestionContext = `${activeApp?.id ?? 'none'}:${isBackOffice ? 'backoffice' : 'client'}`;
    const suggestionContextRef = useRef(suggestionContext);
    const suggestionDraftContextRef = useRef<string | null>(null);
    suggestionContextRef.current = suggestionContext;

    useEffect(() => {
        if (
            !suggestionDraftContextRef.current ||
            suggestionDraftContextRef.current === suggestionContext
        ) {
            return;
        }

        setMessageText('');
        setSuggestionHint('');
        suggestionDraftContextRef.current = null;
    }, [suggestionContext]);

    const openDocumentPreview = async (documentId: number, fileName: string) => {
        if (!activeApp?.id) {
            return;
        }

        try {
            const resp = await api.documentResource.getDocumentPreviewUrl(
                activeApp.id.toString(),
                documentId
            );
            setPreviewFile({
                fileUrl: resp.data.url,
                fileName,
            });
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('failedToLoadDocument')),
                    type: 'error',
                })
            );
        }
    };

    const handleReferenceClick = async (reference: ChatReference) => {
        if (reference.type === 'ticket') {
            try {
                const resp = await api.tickets.getTicket(reference.id.toString());
                setViewTicket(resp.data as TicketInterface);
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('failedToLoadTicket')),
                        type: 'error',
                    })
                );
            }
            return;
        }

        if (reference.type === 'document') {
            await openDocumentPreview(
                reference.id,
                reference.name || `${translate('document')} #${reference.id}`
            );
        }
    };

    const handleViewTicketAction = (action: ACTIONS, ticket: TicketInterface) => {
        if (action === ACTIONS.PREVIEW_FILE && ticket.document_id) {
            void openDocumentPreview(
                Number(ticket.document_id),
                ticket.document_name || translate('document')
            );
        }

        setViewTicket(null);
    };

    const handleSuggestReplyRequest = async () => {
        const requestContext = suggestionContext;
        const response = await onSuggestReply();

        if (!response || requestContext !== suggestionContextRef.current) {
            return;
        }

        const parsedSuggestion = parseReplySuggestion(response);
        setMessageText(parsedSuggestion.draft);
        setSuggestionHint(
            parsedSuggestion.isEmpty
                ? translate('nothingToAnswer')
                : parsedSuggestion.hint
                  ? `${translate('whyThisReply')}: ${parsedSuggestion.hint}`
                  : ''
        );
        suggestionDraftContextRef.current = requestContext;
    };

    const handleSend = (text: string, messageReferences?: ChatReference[]) => {
        onSend(text, messageReferences);
        setMessageText('');
        setSuggestionHint('');
        suggestionDraftContextRef.current = null;
    };

    if (!hasActiveApplication) {
        return (
            <Result
                icon={<ChatBubbleOutlined sx={{ fontSize: 32 }} />}
                title={translate('selectCaseForChat')}
                description={translate('selectCaseForChatDescription')}
            />
        );
    }

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: { xs: 2, sm: 3 },
                    border: '1px solid',
                    borderColor: 'rgba(148, 163, 184, 0.18)',
                    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
                }}
            >
                <ChatHeader
                    users={users}
                    isBackOffice={isBackOffice}
                    canAccessBackoffice={canAccessBackoffice}
                    hasBackofficeAssigned={hasBackofficeAssigned}
                    onToggle={onToggle}
                />
                <ChatMessageList
                    messages={messages}
                    isLoading={isLoading}
                    collapsed={composerSize === 'full'}
                    onDelete={onDelete}
                    onReferenceClick={handleReferenceClick}
                />
                <ChatComposer
                    hasAccess={hasAccess}
                    isSending={isSending}
                    isSuggestingReply={isSuggestingReply}
                    disabled={isLoading}
                    canRetryReplySuggestion={canRetryReplySuggestion}
                    canSuggestReply={canSuggestReply}
                    messageText={messageText}
                    references={references}
                    size={composerSize}
                    suggestionHint={suggestionHint}
                    onMessageTextChange={setMessageText}
                    onSizeChange={setComposerSize}
                    onSend={handleSend}
                    onSuggestReply={handleSuggestReplyRequest}
                />
            </Paper>

            {viewTicket ? (
                <ViewTicket
                    ticket={viewTicket}
                    onClose={() => setViewTicket(null)}
                    handleAction={handleViewTicketAction}
                />
            ) : null}

            {previewFile ? (
                <DocumentPreview
                    open
                    mobile={isMobile}
                    onClose={() => setPreviewFile(null)}
                    title={translate('documentPreview')}
                    fileUrl={previewFile.fileUrl}
                    fileName={previewFile.fileName}
                />
            ) : null}
        </>
    );
};

export default ChatInterface;
